// src/Pages/AdminPage.js
import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import {
  FaTachometerAlt,
  FaBoxOpen,
  FaUsers,
  FaCog,
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaImages,
  FaStore,
  FaMoneyBillWave,
} from 'react-icons/fa';

import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

const menuItems = [
  { label: 'Dashboard', icon: <FaTachometerAlt />, path: '/admin/dashboard' },
  { label: 'Orders', icon: <FaBoxOpen />, path: '/admin/orders' },
  { label: 'Products', icon: <FaBoxOpen />, path: '/admin/products' },
  { label: 'Users', icon: <FaUsers />, path: '/admin/users' },
  { label: 'Product Images', icon: <FaImages />, path: '/admin/ProductImages' },
  { label: 'Add Store', icon: <FaStore />, path: '/admin/AddStore' },
  { label: 'Settings', icon: <FaCog />, path: '/admin/settings' },
];

const AdminPage = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalStores, setTotalStores] = useState(0);
  const [revenue, setRevenue] = useState(0);
  const [dailyData, setDailyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [yearlyData, setYearlyData] = useState([]);

  useEffect(() => {
    const savedLogin = localStorage.getItem('adminLoggedIn');
    if (savedLogin === 'true') {
      setLoggedIn(true);
      fetchAnalytics();
    }
  }, []);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const handleLogin = () => {
    if (username === 'Siraha Nepal' && password === 'hellonepal123') {
      setLoggedIn(true);
      localStorage.setItem('adminLoggedIn', 'true');
      fetchAnalytics();
    } else {
      alert('Invalid Username or Password');
    }
  };

  const handleLogout = () => {
    setLoggedIn(false);
    localStorage.removeItem('adminLoggedIn');
    setUsername('');
    setPassword('');
  };

  const fetchAnalytics = async () => {
    try {
      const ordersSnapshot = await getDocs(collection(db, 'orders'));

      // ✅ Count all orders
      setTotalOrders(ordersSnapshot.size);

      // Revenue is only for delivered orders
      const deliveredOrders = ordersSnapshot.docs.filter(doc => doc.data().status === 'Delivered');

      let totalRevenue = 0;
      const dailyMap = {};
      const monthlyMap = {};
      const yearlyMap = {};

      deliveredOrders.forEach((doc) => {
        const data = doc.data();
        const totalPrice = Number(data.totalPrice) || 0;
        totalRevenue += totalPrice;

        const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : new Date();
        const day = createdAt.toLocaleDateString();
        const month = `${createdAt.getMonth() + 1}-${createdAt.getFullYear()}`;
        const year = `${createdAt.getFullYear()}`;

        dailyMap[day] = (dailyMap[day] || 0) + totalPrice;
        monthlyMap[month] = (monthlyMap[month] || 0) + totalPrice;
        yearlyMap[year] = (yearlyMap[year] || 0) + totalPrice;
      });

      setRevenue(totalRevenue);
      setDailyData(Object.keys(dailyMap).map(key => ({ date: key, revenue: dailyMap[key] })));
      setMonthlyData(Object.keys(monthlyMap).map(key => ({ month: key, revenue: monthlyMap[key] })));
      setYearlyData(Object.keys(yearlyMap).map(key => ({ year: key, revenue: yearlyMap[key] })));

      const usersSnapshot = await getDocs(collection(db, 'users'));
      setTotalUsers(usersSnapshot.size);

      const productsSnapshot = await getDocs(collection(db, 'products'));
      setTotalProducts(productsSnapshot.size);

      const storesSnapshot = await getDocs(collection(db, 'Stores'));
      setTotalStores(storesSnapshot.size);

    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  if (!loggedIn) {
    return (
      <div className="flex h-screen bg-gray-100 justify-center items-center">
        <div className="bg-white p-8 rounded-xl shadow-xl w-80">
          <h2 className="text-2xl font-bold mb-4 text-center">Admin Login</h2>
          <input
            type="text"
            placeholder="Username"
            className="border p-2 w-full rounded mb-3"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <input
            type="password"
            placeholder="Password"
            className="border p-2 w-full rounded mb-5"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            onClick={handleLogin}
            className="bg-gray-900 text-white w-full py-2 rounded hover:bg-gray-700"
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100 relative">
      {/* Mobile Sidebar Toggle */}
      <button
        onClick={toggleSidebar}
        className="fixed top-4 left-4 z-50 md:hidden text-white bg-gray-900 p-2 rounded"
      >
        {sidebarOpen ? <FaTimes /> : <FaBars />}
      </button>

      {/* Sidebar */}
      <aside
        className={`fixed md:static z-40 top-0 left-0 h-full w-64 bg-gray-900 text-white transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0 transition-transform duration-300 ease-in-out`}
      >
        <div className="text-2xl mt-10 font-bold p-6 border-b border-gray-700">Admin Panel</div>
        <nav className="flex-1 p-4 space-y-4">
          {menuItems.map((item) => (
            <Link
              key={item.label}
              to={item.path}
              className="flex items-center gap-3 w-full px-4 py-2 hover:bg-gray-700 rounded"
              onClick={() => setSidebarOpen(false)}
            >
              {item.icon}
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-gray-700">
          <button
            className="flex items-center gap-2 text-red-400 hover:text-red-600"
            onClick={handleLogout}
          >
            <FaSignOutAlt />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-6 overflow-y-auto w-full md:ml-64">
        <h1 className="text-2xl md:text-3xl mt-10 font-semibold mb-6">Welcome, Admin!</h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          <div
            onClick={() => navigate('/admin/orders')}
            className="bg-gradient-to-r from-blue-400 to-blue-600 text-white rounded-lg shadow p-4 cursor-pointer hover:opacity-90 transition"
          >
            <h2 className="text-xl font-semibold mb-2">Total Orders</h2>
            <p className="text-3xl font-bold">{totalOrders}</p>
          </div>

          <div
            onClick={() => navigate('/admin/users')}
            className="bg-gradient-to-r from-green-400 to-green-600 text-white rounded-lg shadow p-4 cursor-pointer hover:opacity-90 transition"
          >
            <h2 className="text-xl font-semibold mb-2">Total Users</h2>
            <p className="text-3xl font-bold">{totalUsers}</p>
          </div>

          <div
            onClick={() => navigate('/admin/products')}
            className="bg-gradient-to-r from-purple-400 to-purple-600 text-white rounded-lg shadow p-4 cursor-pointer hover:opacity-90 transition"
          >
            <h2 className="text-xl font-semibold mb-2">Products Listed</h2>
            <p className="text-3xl font-bold">{totalProducts}</p>
          </div>

          <div
            onClick={() => navigate('/admin/AddStore')}
            className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white rounded-lg shadow p-4 cursor-pointer hover:opacity-90 transition"
          >
            <h2 className="text-xl font-semibold mb-2">Stores Listed</h2>
            <p className="text-3xl font-bold">{totalStores}</p>
          </div>

          <div className="bg-gradient-to-r from-red-400 to-red-600 text-white rounded-lg shadow p-4 col-span-full flex items-center gap-2">
            <FaMoneyBillWave className="text-2xl" />
            <div>
              <h2 className="text-xl font-semibold mb-2">Total Revenue</h2>
              <p className="text-3xl font-bold">₹ {revenue.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-2">Daily Revenue</h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#8884d8" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-2">Monthly Revenue</h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#82ca9d" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-2">Yearly Revenue</h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={yearlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#ffc658" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <Outlet />
      </main>
    </div>
  );
};

export default AdminPage;
